/**
 * EL INTRA Y EL POST EN TRES PASOS: LA REGLA (Jesús, «El intra, paso a paso» y «El
 * postentreno, cerrado», 27-09). Lógica pura; la cara la pone `BuildMealModal`.
 *
 * Vivía dentro de `PeriSugerencias` y el rediseño del 16-10 (PR 5 de Enrique) la volvió a
 * escribir dentro de la hoja nueva. Ahora está aquí una sola vez:
 *   - los pasos de cada uno, en el orden del método, y cuál es opcional (el tercero);
 *   - a qué paso pertenece cada alimento elegido (su marca `bloque_peri`, o la lista de
 *     sugerencias del servidor);
 *   - qué paso se abre: el primer obligatorio sin hacer; hechos los dos, el opcional si
 *     sigue vacío (Jesús, 27-09 22:25: «la persona pincha agua y ya acabaría el tercer
 *     paso»); y con los tres hechos, el último;
 *   - la grasa que le queda al post de sus 4 g (`topeGrasaPost`);
 *   - la consulta de `/api/calculator/peri-sugerencias` y cuántas se enseñan de cada paso
 *     antes de «Ver todos» (lo dice el servidor, `core/peri_sugerencias`).
 */
import { TOPE_GRASA_POST } from './topeGrasaPost';

export const BLOQUES_DEL_PERI = {
    intra: ['proteina', 'hidratos', 'sin_macros'],
    post: ['proteina', 'hidratos', 'bebida'],
};
/** El tercer paso de los dos se puede dejar sin hacer. */
export const esOpcional = (i) => i === 2;

/** Cuántas se enseñan de cada paso si el servidor no lo dice. */
export const VISIBLES_POR_DEFECTO = 3;

const idDe = (f) => f?.alimento_id ?? f?.id ?? f?._id;

/** Una función que dice de qué bloque es cada alimento elegido. */
export const bloqueDelElegido = (bloques) => {
    const deQueBloque = {};
    for (const b of Object.keys(bloques || {})) for (const f of bloques[b] || []) deQueBloque[f.id] = b;
    return (a) => a?.bloque_peri || deQueBloque[idDe(a)];
};

/** Los elegidos que hay en cada paso, en el orden de los pasos. */
export const elegidosPorPaso = (peri, elegidos, bloques) => {
    const deQue = bloqueDelElegido(bloques);
    return BLOQUES_DEL_PERI[peri].map((b) => (elegidos || []).filter((a) => deQue(a) === b));
};

/** El paso que se abre solo (0, 1 o 2), visto lo que ya lleva cada uno. */
export const pasoQueToca = (hechos) => {
    const obligatorio = hechos.findIndex((h, i) => !h && !esOpcional(i));
    if (obligatorio >= 0) return obligatorio;
    const vacio = hechos.findIndex((h) => !h);
    return vacio >= 0 ? vacio : hechos.length - 1;
};

/** La grasa que le queda al post de sus 4 g, a una décima. */
export const grasaQueQuedaAlPost = (elegidos) => Math.round((TOPE_GRASA_POST
    - (elegidos || []).reduce((t, a) => t + (a.macros_efectivos?.G || 0), 0)) * 10) / 10;

/** La dirección de las sugerencias para lo que le falta a la comida. */
export const consultaDeSugerencias = ({ peri, faltaP, faltaH, gRest }) => {
    const q = new URLSearchParams({ peri, p_rest: String(Math.max(0, faltaP || 0)),
                                    h_rest: String(Math.max(0, faltaH || 0)),
                                    g_rest: String(Math.max(0, gRest || 0)) });
    return `/api/calculator/peri-sugerencias?${q}`;
};

/**
 * ¿VA SIN PERIENTRENO? Lo que acaba de contestar manda; si no, lo guardado en su dieta: al
 * recargar las respuestas se pierden y la ficha no lleva este dato (30-10).
 */
export const quiereSinPeri = (respuestas, configDieta) =>
    (respuestas?.pref_perientreno ?? configDieta?.opcion_peri) === 'sin_peri';
